import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import {
  Search,
  Users,
  MessageSquare,
  CreditCard,
  User,
  LogOut,
  Settings,
  Menu,
  X,
  Inbox,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import Logo from '../components/Logo'
import { useNotificationStore } from '../stores/notificationStore'

const navigation = {
  talent: [
    { name: 'My Profile', href: '/profile', icon: User },
    { name: 'My Collaborations', href: '/connections', icon: Inbox },
    { name: 'Messages', href: '/messages', icon: MessageSquare },
    { name: 'Earnings', href: '/payments', icon: CreditCard },
  ],
  seeker: [
    { name: 'My Profile', href: '/profile', icon: User },
    { name: 'Find Experts', href: '/talents', icon: Search },
    { name: 'My Collaborations', href: '/connections', icon: Users },
    { name: 'Messages', href: '/messages', icon: MessageSquare },
    { name: 'Payments', href: '/payments', icon: CreditCard },
  ],
  admin: [
    { name: 'Users', href: '/admin/users', icon: Users },
    { name: 'Settings', href: '/admin/settings', icon: Settings },
  ],
}

export default function DashboardLayout() {
  const { user, logout } = useAuthStore()
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const { unreadMessageCount, collaborationBadgeCount, startPolling, stopPolling } =
    useNotificationStore()

  useEffect(() => {
    startPolling()
    return () => stopPolling()
  }, [startPolling, stopPolling])

  const role = user?.role || 'talent'
  const navItems = navigation[role as keyof typeof navigation] || navigation.talent

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-72 bg-primary-800 transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-20 px-6">
          <Logo height={44} className="brightness-0 invert" />
          <button
            className="lg:hidden p-2 hover:bg-primary-700 rounded-lg text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="px-4 py-6 space-y-1 border-t border-primary-700/50">
          <p className="px-3 mb-3 text-xs font-semibold uppercase tracking-wider text-primary-400">
            Menu
          </p>
          {navItems.map((item) => {
            const isActive = location.pathname === item.href ||
              (item.href !== '/' && location.pathname.startsWith(item.href))
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-accent-500 text-white shadow-lg shadow-accent-500/25'
                    : 'text-primary-200 hover:bg-primary-700/50 hover:text-white'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {item.name}
                {item.href === '/connections' && collaborationBadgeCount > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center ml-auto px-1.5">
                    {collaborationBadgeCount}
                  </span>
                )}
                {item.href === '/messages' && unreadMessageCount > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center ml-auto px-1.5">
                    {unreadMessageCount}
                  </span>
                )}
              </Link>
            )
          })}
        </nav>

        {/* Bottom section with user info */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-primary-700/50">
          {/* User Info */}
          <div className="px-6 py-4">
            <div className="flex items-center gap-3">
              {user?.profilePictureUrl ? (
                <img
                  src={user.profilePictureUrl}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover ring-2 ring-accent-400/30"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center ring-2 ring-accent-400/30">
                  <span className="text-white font-semibold">
                    {user?.email?.[0].toUpperCase()}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate text-sm">
                  {user?.email?.split('@')[0]}
                </p>
                <p className="text-primary-300 text-xs capitalize">
                  {role === 'seeker' ? 'Organization' : role === 'talent' ? 'Expert' : 'Admin'}
                </p>
              </div>
            </div>
          </div>
          {/* Sign Out Button */}
          <div className="px-4 pb-4">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-2.5 w-full rounded-lg text-sm font-medium text-primary-300 hover:bg-primary-700/50 hover:text-white transition-all"
            >
              <LogOut className="h-5 w-5" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-72 min-h-screen">
        {/* Mobile header */}
        <header className="lg:hidden bg-white border-b border-secondary-200 h-16 flex items-center px-4 sticky top-0 z-30">
          <button
            className="p-2 hover:bg-secondary-100 rounded-lg mr-4"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>
          <Logo height={36} />
        </header>

        {/* Page content */}
        <main className="p-6 lg:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
